import If from 'global/ErrorHandlers/If';
import {
  ComponentPropsWithoutRef,
  ReactNode,
  useEffect,
  useState,
} from 'react';

type InfiniteScrollProps = {
  hasMore: boolean;
  refineNext: () => void;
  loader: ReactNode;
  isLoading: boolean;
} & ComponentPropsWithoutRef<'div'>;

const InfiniteScroll = ({
  children,
  hasMore,
  refineNext,
  loader,
  isLoading,
  ...rest
}: InfiniteScrollProps) => {
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const [observer, setObserver] = useState<IntersectionObserver | null>(null);

  useEffect(() => {
    const onSentinelIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && hasMore) {
          refineNext();
        }
      });
    };

    if (hasMore) {
      setObserver(new window.IntersectionObserver(onSentinelIntersection));
    }
  }, [hasMore, refineNext]);

  useEffect(() => {
    if (observer && sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (observer && sentinel) observer.disconnect();
    };
  }, [observer, sentinel]);

  return (
    <div {...rest}>
      {children}
      <If condition={loader && isLoading && hasMore}>{loader}</If>
      <If condition={!isLoading && observer && hasMore}>
        <div
          style={{ display: 'inline' }}
          className="infinite-sentinel"
          ref={(c) => setSentinel(c)}
        />
      </If>
    </div>
  );
};

export default InfiniteScroll;
